import React, { useEffect, useState } from "react";
import Cropper from "react-easy-crop";
import { api, apiErr } from "@/lib/api";
import { uploadImageFile } from "@/lib/pasteImage";
import { Modal, Btn } from "@/components/ui/kit";
import { toast } from "sonner";
import { Loader2, Crop, ZoomIn } from "lucide-react";

export const CARD_ASPECT = 4 / 3;

async function loadBlobUrl(src) {
  const internal = src.startsWith("/api/") || src.startsWith(`${process.env.REACT_APP_BACKEND_URL}/api/`);
  const res = internal
    ? await api.get(src.replace(process.env.REACT_APP_BACKEND_URL, "").replace(/^\/api/, ""), { responseType: "blob" })
    : await api.get("/image-proxy", { params: { url: src }, responseType: "blob" });
  return URL.createObjectURL(res.data);
}

async function cropToFile(blobUrl, area) {
  const img = await new Promise((ok, fail) => { const i = new Image(); i.onload = () => ok(i); i.onerror = fail; i.src = blobUrl; });
  const canvas = document.createElement("canvas");
  canvas.width = 800; canvas.height = 600;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 800, 600);
  ctx.drawImage(img, area.x, area.y, area.width, area.height, 0, 0, 800, 600);
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.88));
  return new File([blob], "recorte.jpg", { type: "image/jpeg" });
}

export const ImageCropModal = ({ src, onClose, onDone }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!src) return undefined;
    let url = null;
    setBlobUrl(null); setCrop({ x: 0, y: 0 }); setZoom(1);
    loadBlobUrl(src).then((u) => { url = u; setBlobUrl(u); }).catch(async (e) => {
      let msg = apiErr(e);
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep msg */ }
      toast.error(`No se pudo cargar la imagen para recortar: ${msg}. Probá copiar la imagen (no el link) y pegarla.`);
      onClose();
    });
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const apply = async () => {
    if (!area) return;
    setBusy(true);
    try { onDone(await uploadImageFile(await cropToFile(blobUrl, area))); toast.success("Imagen recortada"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={!!src} onClose={onClose} title="Recortar y centrar imagen" wide top>
      <div className="space-y-4" data-testid="crop-modal">
        <div className="relative w-full h-[50vh] max-h-[420px] rounded-lg overflow-hidden bg-black">
          {blobUrl ? (
            <Cropper image={blobUrl} crop={crop} zoom={zoom} minZoom={0.5} maxZoom={4} aspect={CARD_ASPECT} restrictPosition={false}
              onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} objectFit="contain" />
          ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando imagen…</div>}
        </div>
        <div className="flex items-center gap-3">
          <ZoomIn className="w-4 h-4 text-muted-foreground" />
          <input type="range" min={0.5} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom" className="flex-1 accent-[hsl(var(--primary))]" />
          <span className="text-xs font-data w-10 text-right">{zoom.toFixed(1)}x</span>
        </div>
        <p className="text-[11px] text-muted-foreground">Arrastrá para centrar y usá el zoom. El recorte tiene la misma forma que las tarjetas del Punto de Venta (4:3). Si alejás la imagen, el espacio sobrante queda en blanco.</p>
        <div className="flex gap-2">
          <Btn variant="outline" onClick={onClose} className="flex-1" data-testid="crop-skip">Usar sin recortar</Btn>
          <Btn onClick={apply} disabled={!blobUrl || busy} className="flex-1" data-testid="crop-apply">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crop className="w-4 h-4" />} Recortar y usar</Btn>
        </div>
      </div>
    </Modal>
  );
};
